import React, { useCallback, useState } from 'react';
import Popover from 'antd/lib/popover';
import { DownOutlined } from '@ant-design/icons';
import { Row, Col } from 'antd/lib/grid';
import Checkbox, { CheckboxChangeEvent } from 'antd/lib/checkbox';
import Text from 'antd/lib/typography/Text';
import InputNumber from 'antd/lib/input-number';
import Select from 'antd/lib/select';
import Slider from 'antd/lib/slider';
import Button from 'antd/lib/button';

import { GridColor } from 'reducers';
import { ChunkQuality } from 'cvat-core-wrapper';
import { ImageFilterAlias } from 'utils/image-processing';
import {
    DisplaySettings,
    DisplaySettingsPlayer,
    SerializedImageFilter,
    defaultDisplaySettings,
} from 'utils/display-settings';
import { clamp } from 'utils/math';
import { DepthVisualizationOptions } from 'components/annotation-page/canvas/views/canvas2d/depth-visualization-filter';

const minGridSize = 5;
const maxGridSize = 1000;

const defaultDepthOptions: DepthVisualizationOptions = {
    showDepth: true,
    showInvalidDepth: false,
    opacity: 0.5,
    colorMap: 'jet',
    minDepth: 0,
    maxDepth: 4000,
};

interface Props {
    value: DisplaySettings;
    onChange: (next: DisplaySettings) => void;
}

function findFilter(filters: SerializedImageFilter[], alias: ImageFilterAlias): SerializedImageFilter | null {
    return filters.find((f) => f.alias === alias) ?? null;
}

function withFilter(
    filters: SerializedImageFilter[],
    alias: ImageFilterAlias,
    options: Record<string, any> | null,
): SerializedImageFilter[] {
    const rest = filters.filter((f) => f.alias !== alias);
    if (options === null) return rest;
    return [...rest, { alias, options }];
}

export default function ProjectDisplaySettings(props: Props): JSX.Element {
    const { value, onChange } = props;

    // Draft holds the current edit state. We don't re-sync from `value` on
    // prop changes: after each commit the parent saves and pushes the same
    // value back, and re-syncing mid-drag would make sliders jump.
    const [draft, setDraft] = useState<DisplaySettings>(value);
    const [open, setOpen] = useState(false);

    const { player, imageFilters, chunkQuality } = draft;

    const commit = useCallback((next: DisplaySettings) => {
        setDraft(next);
        onChange(next);
    }, [onChange]);

    const draftPlayer = useCallback((patch: Partial<DisplaySettingsPlayer>) => {
        setDraft((prev) => ({ ...prev, player: { ...prev.player, ...patch } }));
    }, []);

    const commitPlayer = useCallback((patch: Partial<DisplaySettingsPlayer>) => {
        setDraft((prev) => {
            const next = { ...prev, player: { ...prev.player, ...patch } };
            onChange(next);
            return next;
        });
    }, [onChange]);

    const gammaFilter = findFilter(imageFilters, ImageFilterAlias.GAMMA_CORRECTION);
    const depthFilter = findFilter(imageFilters, ImageFilterAlias.DEPTH_VISUALIZATION);
    const gamma: number = typeof gammaFilter?.options?.gamma === 'number' ? gammaFilter.options.gamma : 1;
    const depthOptions: DepthVisualizationOptions = {
        ...defaultDepthOptions,
        ...(depthFilter?.options ?? {}),
    };

    const draftGamma = (newGamma: number): void => {
        setDraft((prev) => ({
            ...prev,
            imageFilters: newGamma === 1
                ? withFilter(prev.imageFilters, ImageFilterAlias.GAMMA_CORRECTION, null)
                : withFilter(prev.imageFilters, ImageFilterAlias.GAMMA_CORRECTION, { gamma: newGamma }),
        }));
    };

    const commitGamma = (newGamma: number): void => {
        setDraft((prev) => {
            const next: DisplaySettings = {
                ...prev,
                imageFilters: newGamma === 1
                    ? withFilter(prev.imageFilters, ImageFilterAlias.GAMMA_CORRECTION, null)
                    : withFilter(prev.imageFilters, ImageFilterAlias.GAMMA_CORRECTION, { gamma: newGamma }),
            };
            onChange(next);
            return next;
        });
    };

    const onChangeDepthOption = (patch: Partial<DepthVisualizationOptions>, persist: boolean): void => {
        setDraft((prev) => {
            const prevOptions: DepthVisualizationOptions = {
                ...defaultDepthOptions,
                ...(findFilter(prev.imageFilters, ImageFilterAlias.DEPTH_VISUALIZATION)?.options ?? {}),
            };
            const next: DisplaySettings = {
                ...prev,
                imageFilters: withFilter(
                    prev.imageFilters,
                    ImageFilterAlias.DEPTH_VISUALIZATION,
                    { ...prevOptions, ...patch },
                ),
            };
            if (persist) onChange(next);
            return next;
        });
    };

    const onToggleDepth = (checked: boolean): void => {
        setDraft((prev) => {
            const prevOptions: DepthVisualizationOptions = {
                ...defaultDepthOptions,
                ...(findFilter(prev.imageFilters, ImageFilterAlias.DEPTH_VISUALIZATION)?.options ?? {}),
            };
            const next: DisplaySettings = checked
                ? {
                    ...prev,
                    imageFilters: withFilter(
                        prev.imageFilters,
                        ImageFilterAlias.DEPTH_VISUALIZATION,
                        { ...prevOptions, showDepth: true },
                    ),
                }
                : {
                    ...prev,
                    imageFilters: withFilter(prev.imageFilters, ImageFilterAlias.DEPTH_VISUALIZATION, null),
                };
            onChange(next);
            return next;
        });
    };

    const onResetColors = (): void => {
        commit({
            ...draft,
            player: {
                ...draft.player,
                brightnessLevel: defaultDisplaySettings.player.brightnessLevel,
                contrastLevel: defaultDisplaySettings.player.contrastLevel,
                saturationLevel: defaultDisplaySettings.player.saturationLevel,
            },
            imageFilters: draft.imageFilters.filter((f) => f.alias !== ImageFilterAlias.GAMMA_CORRECTION),
        });
    };

    const content = (
        <div className='cvat-canvas-image-setups-content'>
            <Text strong>Image grid</Text>
                    <hr />
                    <Row justify='space-between' align='middle' gutter={8}>
                        <Col span={1} />
                        <Col span={6}><Text className='cvat-text-color'> Size </Text></Col>
                        <Col span={8}><Text className='cvat-text-color'> Color </Text></Col>
                        <Col span={8}><Text className='cvat-text-color'> Opacity </Text></Col>
                    </Row>
                    <Row justify='space-between' align='middle' gutter={8}>
                        <Col span={1}>
                            <Checkbox
                                className='cvat-text-color cvat-image-setups-grid'
                                checked={player.grid}
                                onChange={(e: CheckboxChangeEvent) => commitPlayer({ grid: e.target.checked })}
                            />
                        </Col>
                        <Col span={6} className='cvat-image-setups-grid-size'>
                            <InputNumber
                                className='cvat-image-setups-grid-size-input'
                                min={minGridSize}
                                max={maxGridSize}
                                value={player.gridSize}
                                disabled={!player.grid}
                                onChange={(v) => {
                                    if (v !== null && v !== undefined) {
                                        commitPlayer({ gridSize: Math.floor(clamp(+v, minGridSize, maxGridSize)) });
                                    }
                                }}
                            />
                        </Col>
                        <Col span={8} className='cvat-image-setups-grid-color'>
                            <Select
                                className='cvat-image-setups-grid-color-input'
                                value={player.gridColor}
                                disabled={!player.grid}
                                onChange={(color: GridColor) => commitPlayer({ gridColor: color })}
                            >
                                <Select.Option value={GridColor.White}>White</Select.Option>
                                <Select.Option value={GridColor.Black}>Black</Select.Option>
                                <Select.Option value={GridColor.Red}>Red</Select.Option>
                                <Select.Option value={GridColor.Green}>Green</Select.Option>
                                <Select.Option value={GridColor.Blue}>Blue</Select.Option>
                            </Select>
                        </Col>
                        <Col span={8} className='cvat-image-setups-grid-opacity'>
                            <Slider
                                className='cvat-image-setups-grid-opacity-input'
                                min={0}
                                max={100}
                                value={player.gridOpacity}
                                disabled={!player.grid}
                                onChange={(v: number | [number, number]) => draftPlayer({ gridOpacity: v as number })}
                                onAfterChange={(v: number | [number, number]) => commitPlayer({ gridOpacity: v as number })}
                            />
                        </Col>
                    </Row>
                    <Text strong>Color settings</Text>
                    <hr />
                    <Row className='cvat-image-setups-brightness'>
                        <Col span={6}><Text className='cvat-text-color'> Brightness </Text></Col>
                        <Col span={12}>
                            <Slider
                                min={50}
                                max={200}
                                value={player.brightnessLevel}
                                onChange={(v: number | [number, number]) => draftPlayer({ brightnessLevel: v as number })}
                                onAfterChange={(v: number | [number, number]) => commitPlayer({ brightnessLevel: v as number })}
                            />
                        </Col>
                    </Row>
                    <Row className='cvat-image-setups-contrast'>
                        <Col span={6}><Text className='cvat-text-color'> Contrast </Text></Col>
                        <Col span={12}>
                            <Slider
                                min={50}
                                max={200}
                                value={player.contrastLevel}
                                onChange={(v: number | [number, number]) => draftPlayer({ contrastLevel: v as number })}
                                onAfterChange={(v: number | [number, number]) => commitPlayer({ contrastLevel: v as number })}
                            />
                        </Col>
                    </Row>
                    <Row className='cvat-image-setups-saturation'>
                        <Col span={6}><Text className='cvat-text-color'> Saturation </Text></Col>
                        <Col span={12}>
                            <Slider
                                min={0}
                                max={300}
                                value={player.saturationLevel}
                                onChange={(v: number | [number, number]) => draftPlayer({ saturationLevel: v as number })}
                                onAfterChange={(v: number | [number, number]) => commitPlayer({ saturationLevel: v as number })}
                            />
                        </Col>
                    </Row>
                    <Row className='cvat-image-setups-gamma'>
                        <Col span={6}><Text className='cvat-text-color'> Gamma </Text></Col>
                        <Col span={12}>
                            <Slider
                                min={0.2}
                                max={2.6}
                                step={0.01}
                                value={gamma}
                                onChange={(v: number | [number, number]) => draftGamma(v as number)}
                                onAfterChange={(v: number | [number, number]) => commitGamma(v as number)}
                            />
                        </Col>
                    </Row>
                    <Row justify='space-around'>
                        <Col>
                            <Button onClick={onResetColors}>Reset color settings</Button>
                        </Col>
                    </Row>
                    <Text strong>Image quality</Text>
                    <hr />
                    <Row justify='space-between' align='middle'>
                        <Col>
                            <Checkbox
                                checked={chunkQuality === ChunkQuality.ORIGINAL}
                                onChange={(e: CheckboxChangeEvent) => commit({
                                    ...draft,
                                    chunkQuality: e.target.checked ? ChunkQuality.ORIGINAL : ChunkQuality.COMPRESSED,
                                })}
                            >
                                Uncompressed
                            </Checkbox>
                        </Col>
                    </Row>
                    <div className='cvat-depth-visualization-content'>
                        <Text strong>Depth visualization</Text>
                        <hr />
                        <Row justify='space-between' align='middle'>
                            <Col>
                                <Checkbox
                                    checked={!!depthFilter}
                                    onChange={(e) => onToggleDepth(e.target.checked)}
                                >
                                    Show depth overlay
                                </Checkbox>
                            </Col>
                            <Col>
                                <Checkbox
                                    checked={depthOptions.showInvalidDepth}
                                    disabled={!depthFilter}
                                    onChange={(e) => onChangeDepthOption({ showInvalidDepth: e.target.checked }, true)}
                                >
                                    Show invalid pixels
                                </Checkbox>
                            </Col>
                        </Row>
                        <Row align='middle'>
                            <Col span={6}><Text className='cvat-text-color'>Opacity</Text></Col>
                            <Col span={18}>
                                <Slider
                                    min={0}
                                    max={1}
                                    step={0.01}
                                    disabled={!depthFilter}
                                    value={depthOptions.opacity}
                                    onChange={(v: number | [number, number]) => onChangeDepthOption({ opacity: v as number }, false)}
                                    onAfterChange={(v: number | [number, number]) => onChangeDepthOption({ opacity: v as number }, true)}
                                />
                            </Col>
                        </Row>
                        <Row align='middle'>
                            <Col span={6}><Text className='cvat-text-color'>Color Map</Text></Col>
                            <Col span={18}>
                                <Select
                                    value={depthOptions.colorMap}
                                    disabled={!depthFilter}
                                    onChange={(v) => onChangeDepthOption({ colorMap: v }, true)}
                                    className='cvat-depth-colormap-select'
                                >
                                    <Select.Option value='jet'>Jet</Select.Option>
                                    <Select.Option value='viridis'>Viridis</Select.Option>
                                    <Select.Option value='greys'>Grayscale</Select.Option>
                                </Select>
                            </Col>
                        </Row>
                        <Row align='middle'>
                            <Col span={6}><Text className='cvat-text-color'>Min Depth</Text></Col>
                            <Col span={18}>
                                <InputNumber
                                    min={0}
                                    max={depthOptions.maxDepth}
                                    value={depthOptions.minDepth}
                                    disabled={!depthFilter}
                                    onChange={(v) => onChangeDepthOption({ minDepth: v as number }, true)}
                                    className='cvat-depth-range-input'
                                />
                            </Col>
                        </Row>
                        <Row align='middle'>
                            <Col span={6}><Text className='cvat-text-color'>Max Depth</Text></Col>
                            <Col span={18}>
                                <InputNumber
                                    min={depthOptions.minDepth}
                                    value={depthOptions.maxDepth}
                                    disabled={!depthFilter}
                                    onChange={(v) => onChangeDepthOption({ maxDepth: v as number }, true)}
                                    className='cvat-depth-range-input'
                                />
                            </Col>
                        </Row>
                    </div>
        </div>
    );

    return (
        <div className='cvat-project-display-settings'>
            <Popover
                destroyTooltipOnHide
                trigger='click'
                placement='right'
                overlayInnerStyle={{ padding: 0 }}
                content={content}
                open={open}
                onOpenChange={setOpen}
            >
                <DownOutlined
                    className={`cvat-project-display-settings-trigger${open ? ' cvat-project-display-settings-trigger-open' : ''}`}
                />
            </Popover>
        </div>
    );
}
